import * as React from 'react';
import { IconType } from 'react-icons';
import {
  GetOptionFlowQuery,
  GetUserForDashboardQuery,
  GetWeeklyOverviewQuery,
  GetDarkPoolTradesBaselineQuery,
  GetDarkPoolTradesFullQuery,
  GetDarkPoolTradesQuery,
  GetHistoricalDarkPoolBaselineQuery,
  GetHistoricalDarkPoolWithTickersBaselineQuery,
} from 'types/generated/client';
import { Viewer } from 'constants/user';
import { RawTrade } from 'utils/shared/trades/types';
import type { FlowTickerInfo } from 'constants/payloads/realtime';
import { TickerAggregatedDetails } from 'components/OptionAggregatedTable/props';
import { FlowTableProps } from 'utils/shared/trades/types';
import type { FlowMetricMode, FlowSideBasisSelection } from 'utils/shared/trades/presets';
import type { DelayedFlowWait } from 'components/DelayedFlowNotice/delayedFlowNoticeModel';

export {
  DEFAULT_FLOW_KEY_INDEX,
  FLOW_TABLE_HEADERS,
  DEFAULT_FLOW_KEY,
} from 'components/OptionFlowTable/props';

export enum LayoutType {
  Stacked = 0,
  Market = 1,
  Flow = 2,
}

export enum FeedType {
  Smart = 'SMART',
  Full = 'FULL',
  Dark = 'DARK',
  Lit = 'LIT',
  All = 'ALL',
}

export { DEFAULT_AGGREGATE_KEY } from 'components/OptionAggregatedTable/props';

export interface OwnProps {}

export interface Props extends OwnProps {
  viewer: Viewer;
  title: string;
  Icon: IconType;
  trades?: RawTrade[];
  // Realtime only: ticker-level info the DO sends once per ticker (not inlined per row).
  // When present, `trades` are raw FlowRows and the ~17 ticker fields are joined in the
  // single derive pass in FlowPageContainer — avoiding a second full adapted array of every
  // trade (memory: 3 resident copies → 2). Absent for legacy/injected/other surfaces, whose
  // rows already carry tickerDetails + ticker fields inline.
  tickerInfo?: Record<string, FlowTickerInfo>;
  dailyVolume?: GetOptionFlowQuery['daily_volume'];
  loading?: boolean;
  called?: boolean;
  userDetails?: GetUserForDashboardQuery['users_by_pk'] | null;
  OptionFlowTable: React.FC<React.PropsWithChildren<FlowTableProps>>;
  shouldShowDatePicker: boolean;
  startDate?: string;
  setStartDate?: React.Dispatch<React.SetStateAction<string>>;
  endDate?: string;
  setEndDate?: React.Dispatch<React.SetStateAction<string>>;
  tickers?: string;
  setTickers?: React.Dispatch<React.SetStateAction<string>>;
  maxResults?: number | null;
  setMaxResults?: React.Dispatch<React.SetStateAction<number | null>>;
  handleHistoricalSearch?: (flowSideBasis: FlowSideBasisSelection) => void;
  onFlowSideBasisChange?: (flowSideBasis: FlowSideBasisSelection) => void;
  // Command center "live count" preview: rows for the previewed side, fetched into
  // a dataset that never feeds the committed table. See useFlowSidePreviewCount.
  previewTrades?: RawTrade[];
  isPreviewLoading?: boolean;
  onFlowSidePreviewRequest?: (tradeSides: string[]) => void;
  // Baseline ("full") feed toggle, surfaced in the command center. Owned by the page
  // (which holds the realtime hook) and drilled through to CommandCenter. `showBaselineToggle`
  // controls visibility (flag + realtime active, free + paid); `canUseBaseline` gates use (paid).
  showBaselineToggle?: boolean;
  canUseBaseline?: boolean;
  showBaseline?: boolean;
  onToggleBaseline?: (next: boolean) => void;
  flowSliderReplacement?: React.ReactNode;
  defaultLayoutType?: LayoutType;
  injectedPageData?: GetOptionFlowQuery | GetWeeklyOverviewQuery;
  isWeekly?: boolean;
  pageHref: string;
  isShowFlowDescription?: boolean;
  totalFlowCount?: number;
  // Free viewers only: set while the delayed realtime feed is connected but holds no trades yet.
  // The tables explain the 30-minute wait.
  delayedFlowWait?: DelayedFlowWait | null;
  darkPoolTrades?:
    | GetDarkPoolTradesQuery['equity_print_flow']
    | GetHistoricalDarkPoolBaselineQuery['equity_print_feed_baseline']
    | GetHistoricalDarkPoolWithTickersBaselineQuery['equity_print_feed_baseline'];
  darkPoolLoading?: boolean;
  darkPoolCalled?: boolean;
}

export interface TopTickerProps {
  tradesByTicker: TickerAggregatedDetails[];
  presetGraph: number;
  metricMode?: FlowMetricMode;
}

export interface TopTickerGraphProps {
  topTrades: TickerAggregatedDetails[];
}

export interface SectorAggregatedDetails {
  sector: string;
  putFlow: number;
  otm: number;
  contracts: number;
  premium: number;
  expiry: number;
  unusualOtm: number;
  unusualContracts: number;
  unusualPremium: number;
  sweepOtm: number;
  sweepContracts: number;
  sweepPremium: number;
  splitOtm: number;
  splitContracts: number;
  splitPremium: number;
  blockOtm: number;
  blockContracts: number;
  blockPremium: number;
  goldenSweepOtm: number;
  goldenSweepContracts: number;
  goldenSweepPremium: number;
  bullishPremium: number;
  bearishPremium: number;
  neutralPremium: number;
  sentimentScore: number;
  netPremium: number;
  netPremiumShare: number;
}

export type DarkPoolTrade = (
  | GetDarkPoolTradesQuery['equity_print_flow'][number]
  | GetDarkPoolTradesBaselineQuery['equity_print_feed_baseline'][number]
  | GetDarkPoolTradesFullQuery['equity_print_flow_full'][number]
  | GetHistoricalDarkPoolBaselineQuery['equity_print_feed_baseline'][number]
  | GetHistoricalDarkPoolWithTickersBaselineQuery['equity_print_feed_baseline'][number]
) & {
  tickerDetails?: {
    name?: string | null;
    ticker?: string | null;
    sector?: string | null;
  } | null;
};

export interface DarkPoolSectorAggregatedDetails {
  sector: string;
  premium: number;
  volume: number;
  lateBuyCount: number;
  lateSellCount: number;
  goldenTradeCount: number;
  isEtf: boolean;
  avgVolume: number;
  dayVolume: number;
  marketCap: number;
}

export interface DarkPoolTickerAggregatedDetails extends DarkPoolSectorAggregatedDetails {
  ticker: string;
  tickerName: string;
  momentum: number;
}

export interface DarkPoolGraphProps {
  tradesByTicker: DarkPoolTickerAggregatedDetails[];
  tradesBySector: DarkPoolSectorAggregatedDetails[];
  presetGraph: number;
}

export interface SectorGraphProps {
  tradesBySector: SectorAggregatedDetails[];
  isUnusualFiltered: boolean;
  isSweepsFiltered: boolean;
  activeSectorGraph: number;
  setActiveSectorGraph: (index: number) => void;
  metricMode?: FlowMetricMode;
}

export interface DarkPoolSectorGraphProps {
  tradesBySector: DarkPoolSectorAggregatedDetails[];
}

export interface BarChartProps {
  topTrades: TickerAggregatedDetails[];
  color: string;
  label: string;
  prefix: string;
  postfix: string;
  sortKey: string;
  formatter: (num: number) => string | number;
  useSignedColors?: boolean;
  /** Bar color for non-negative values when useSignedColors is on. Defaults to the bullish green. */
  bullishColor?: string;
  /** Bar color for negative values when useSignedColors is on. Defaults to the bearish red. */
  bearishColor?: string;
}

export interface DarkPoolBarChartProps {
  topTrades: DarkPoolTickerAggregatedDetails[];
  color: string;
  label: string;
  prefix: string;
  postfix: string;
  sortKey: string;
  formatter: (num: number) => string | number;
  yTickOptions?: object;
}
